import { memo, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'
import { Icon, Text } from 'react-native-paper'
import {
    FOCUS_DIM_OPACITY,
    RECENCY_FULL_OPACITY_MINUTES,
    RECENCY_MIN_OPACITY,
    ZOOM_HIDE_PATHS_THRESHOLD,
} from '@/constants/monitor'
import { hexToRgba } from '@/lib/color-utils'
import { computeSegmentOpacity } from '@/lib/map-utils'
import { getRotationStyle } from '@/lib/movement-utils'
import type { MapCoordinate, PathMarkerData, UserPathData } from '@/types/mapData'
import { Callout, Marker, Polyline } from '@/ui/MapView'

interface RecencySegment {
    coordinates: MapCoordinate[]
    opacity: number
}

function buildRecencySegments(
    coordinates: MapCoordinate[],
    timestamps: string[],
    referenceTime: Date
): RecencySegment[] {
    if (coordinates.length < 2) return []

    const segments: RecencySegment[] = []
    const opacityBucket = (o: number) => Math.round(o * 10) / 10

    let currentCoords: MapCoordinate[] = [coordinates[0]]
    let currentBucket = opacityBucket(
        computeSegmentOpacity(
            timestamps[0],
            referenceTime,
            RECENCY_FULL_OPACITY_MINUTES,
            RECENCY_MIN_OPACITY
        )
    )

    for (let i = 1; i < coordinates.length; i++) {
        const segOpacity = opacityBucket(
            computeSegmentOpacity(
                timestamps[i],
                referenceTime,
                RECENCY_FULL_OPACITY_MINUTES,
                RECENCY_MIN_OPACITY
            )
        )

        if (segOpacity === currentBucket) {
            currentCoords.push(coordinates[i])
        } else {
            currentCoords.push(coordinates[i])
            segments.push({ coordinates: currentCoords, opacity: currentBucket })
            currentCoords = [coordinates[i]]
            currentBucket = segOpacity
        }
    }

    if (currentCoords.length >= 2) {
        segments.push({ coordinates: currentCoords, opacity: currentBucket })
    }

    return segments
}

interface PathPolylineProps {
    path: UserPathData
    focusOpacity: number
    referenceTime: Date
}

export const PathPolyline = memo(function PathPolyline({
    path,
    focusOpacity,
    referenceTime,
}: PathPolylineProps) {
    const segments = useMemo(
        () => buildRecencySegments(path.coordinates, path.timestamps, referenceTime),
        [path.coordinates, path.timestamps, referenceTime]
    )

    return (
        <>
            {segments.map(seg => {
                const combinedOpacity = seg.opacity * focusOpacity
                const first = seg.coordinates[0]
                return (
                    <Polyline
                        key={`${path.userId}-${first.latitude}-${first.longitude}`}
                        coordinates={seg.coordinates}
                        strokeColor={hexToRgba(path.userColor, combinedOpacity)}
                        strokeWidth={3}
                    />
                )
            })}
        </>
    )
})

interface PathMarkerItemProps {
    marker: PathMarkerData
    mapHeading?: number
    opacity?: number
}

export const PathMarkerItem = memo(function PathMarkerItem({
    marker,
    mapHeading = 0,
    opacity = 1,
}: PathMarkerItemProps) {
    const rotationStyle = useMemo(
        () => getRotationStyle(marker.rotation - mapHeading, marker.shouldFlip),
        [marker.rotation, marker.shouldFlip, mapHeading]
    )

    return (
        <Marker
            coordinate={marker.coordinate}
            anchor={{ x: 0.5, y: 0.5 }}
            tracksViewChanges={true}
            opacity={opacity}
        >
            <View style={rotationStyle}>
                <Icon source={marker.icon} size={20} color={marker.userColor} />
            </View>
            <Callout>
                <View style={styles.callout}>
                    <Text style={[styles.calloutLabel, { color: marker.userColor }]}>
                        {marker.userName}
                    </Text>
                    <Text style={styles.calloutBody}>{marker.description}</Text>
                </View>
            </Callout>
        </Marker>
    )
})

interface UserPathMarkersProps {
    paths: UserPathData[]
    mapHeading?: number
    focusedUserId: string | null
    zoomLevel: number | null
    referenceTime: Date
}

export const UserPathMarkers = memo(function UserPathMarkers({
    paths,
    mapHeading = 0,
    focusedUserId,
    zoomLevel,
    referenceTime,
}: UserPathMarkersProps) {
    const isZoomedOut = zoomLevel !== null && zoomLevel > ZOOM_HIDE_PATHS_THRESHOLD

    return (
        <>
            {paths.map(path => {
                const isFocused = path.userId === focusedUserId
                if (isZoomedOut && focusedUserId !== null && !isFocused) return null

                const focusOpacity = focusedUserId === null ? 1 : isFocused ? 1 : FOCUS_DIM_OPACITY

                return (
                    <View key={path.userId}>
                        <PathPolyline
                            path={path}
                            focusOpacity={focusOpacity}
                            referenceTime={referenceTime}
                        />
                        {focusOpacity > FOCUS_DIM_OPACITY &&
                            path.pathMarkers.map(marker => (
                                <PathMarkerItem
                                    key={marker.id}
                                    marker={marker}
                                    mapHeading={mapHeading}
                                    opacity={focusOpacity}
                                />
                            ))}
                    </View>
                )
            })}
        </>
    )
})

export const pathMarkerStyles = StyleSheet.create({
    callout: {
        padding: 8,
        minWidth: 120,
        borderRadius: 8,
        backgroundColor: 'white',
    },
    calloutLabel: {
        fontSize: 12,
        fontWeight: '500',
    },
    calloutBody: {
        fontSize: 12,
        color: '#000',
    },
})

const styles = pathMarkerStyles
